import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { getBlogArticle } from "../../lib/getBlogArticle";
import BlogArticleSkeleton from "../../components/Skeletons/BlogArticleSkeleton/BlogArticleSkeleton";
import styles from "./BlogArticle.module.css";
import { ArrowUpward } from "@mui/icons-material";
import DOMPurify from "dompurify";
import ProductSlider6 from "../../components/Sliders/ProductSliders/ProductSlider6/ProductSlider6";
import ProductImage from "../../components/common/ProductImage/ProductImage";
import BlogSEO from "../../components/SEO/BlogSEO";

interface BlockProps {
  block: any;
  imageBaseUrl: string | undefined;
}

interface SrcSetEntry {
  url: string;
  size?: string;
}

const calculateReadingTime = (content: string): number => {
  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  return Math.ceil(words / wordsPerMinute);
};

const RichTextBlock: React.FC<BlockProps> = ({ block, imageBaseUrl }) => {
  const content = block.content || "";

  const processContent = (html: string) => {
    if (!imageBaseUrl) return html;

    return html.replace(
      /<img[^>]+src="(http:\/\/localhost:1337)?\/uploads\/([^"]+)"[^>]*>/g,
      (match, host, path) => {
        const cleanMatch = match.replace(/srcset="[^"]*"/, "");
        return cleanMatch
          .replace(
            /src="[^"]+"/g,
            `src="${imageBaseUrl}/small_${path.split("/").pop()}"`
          )
          .replace(/class="[^"]*"/g, "")
          .replace("<img", '<img class="contentImage"');
      }
    );
  };

  const sanitizeConfig = {
    ALLOWED_TAGS: [
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "p",
      "br",
      "hr",
      "ul",
      "ol",
      "li",
      "strong",
      "em",
      "img",
      "a",
    ],
    ALLOWED_ATTR: ["href", "src", "alt", "width", "height", "class"],
  };

  const processedContent = processContent(content);
  const sanitizedContent = DOMPurify.sanitize(processedContent, sanitizeConfig);

  return (
    <div
      className={styles.richText}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};

const QuoteBlock: React.FC<BlockProps> = ({ block }) => (
  <blockquote className={styles.quote}>
    <p>{block.text}</p>
    {block.author && <cite>{block.author}</cite>}
  </blockquote>
);

const MediaBlock: React.FC<BlockProps> = ({ block, imageBaseUrl }) => (
  <div className={styles.media}>
    {block.file && imageBaseUrl && (
      <img
        src={`${imageBaseUrl}${block.file.url}`}
        alt=""
        className={styles.mediaImage}
      />
    )}
  </div>
);

const SliderBlock: React.FC<BlockProps> = ({ block, imageBaseUrl }) => (
  <div className={styles.slider}>
    {block.slides?.map((slide: any) => (
      <div key={slide.id} className={styles.slide}>
        {slide.image && imageBaseUrl && (
          <img
            src={`${imageBaseUrl}${slide.image.url}`}
            alt={slide.title}
            className={styles.slideImage}
          />
        )}
        {slide.title && <h3>{slide.title}</h3>}
        {slide.description && <p>{slide.description}</p>}
      </div>
    ))}
  </div>
);

const BlogArticle: React.FC = () => {
  const { blogUrl } = useParams();
  const [article, setArticle] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const imageBaseUrl = process.env.REACT_APP_IMAGE_FOLDER;
  const [readingProgress, setReadingProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const articleRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (blogUrl) {
      window.scrollTo(0, 0);
      setLoading(true);
      getBlogArticle(blogUrl)
        .then((articleData) => {
          setArticle(articleData);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching article:", error);
          setLoading(false);
        });
    }
  }, [blogUrl]);

  useEffect(() => {
    const handleScroll = () => {
      if (!articleRef.current) return;

      const element = articleRef.current;
      const totalHeight = element.clientHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setReadingProgress(Math.min(100, Math.max(0, progress)));

      setShowBackToTop(window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return <BlogArticleSkeleton />;
  }

  if (!article) return null;

  console.log("BlogArticle component rendering", JSON.stringify(article));
  console.log("Article blocks:", article?.blocks);

  const renderBlock = (block: any) => {
    switch (block.type) {
      case "RichText":
        return <RichTextBlock block={block} imageBaseUrl={imageBaseUrl} />;
      case "Quote":
        return <QuoteBlock block={block} imageBaseUrl={imageBaseUrl} />;
      case "Media":
        return <MediaBlock block={block} imageBaseUrl={imageBaseUrl} />;
      case "Slider":
        return <SliderBlock block={block} imageBaseUrl={imageBaseUrl} />;
      default:
        return null;
    }
  };

  const readingTime = calculateReadingTime(article.blocks?.[0]?.content || "");

  return (
    <>
      {/* SEO */}
      <BlogSEO article={article} />

      {/* Reading progress bar */}
      <div
        className={styles.progressBar}
        style={{ width: `${readingProgress}%` }}
        aria-hidden="true"
      />

      <article
        ref={articleRef}
        className={styles.article}
        aria-label={`Artículo del blog: ${article.title}`}
      >
        {article.cover && imageBaseUrl && (
          <div
            className={styles.cover}
            aria-label="Imagen de portada del artículo"
          >
            <img
              src={`${article.cover.url}`}
              alt={article.title}
              className={styles.coverImage}
            />
          </div>
        )}

        <div className={styles.header} aria-label="Encabezado del artículo">
          <div
            className={styles.categories}
            aria-label="Categorías del artículo"
          >
            {article.blog_categories?.map((category: any) => (
              <span key={category.id} className={styles.category}>
                {category.name}
              </span>
            ))}
          </div>
          <h1 className={styles.title}>{article.title}</h1>

          <div className={styles.articleMeta}>
            <span className={styles.readingTime}>
              {readingTime} min lectura
            </span>
            {article.blog_author && (
              <div className={styles.author} aria-label="Autor del artículo">
                {article.blog_author.avatar && imageBaseUrl && (
                  <img
                    src={`${article.blog_author.avatar.url}`}
                    alt={article.blog_author.name}
                    className={styles.authorAvatar}
                  />
                )}
                <span className={styles.authorName}>
                  Por {article.blog_author.name}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className={styles.content} aria-label="Contenido del artículo">
          {article.blocks?.map((block: any) => {
            console.log("Processing block:", block);
            return (
              <div key={block.id} className={styles.block}>
                {renderBlock(block)}
              </div>
            );
          })}
        </div>

        {article.products && article.products.length > 0 && (
          <div
            className={styles.relatedProducts}
            aria-label="Productos relacionados"
          >
            <ProductSlider6
              products={article.products.map((product: any) => ({
                ...product,
                image: (
                  <ProductImage
                    src={`${imageBaseUrl}${product.image.url}`}
                    alt={product.name}
                  />
                ),
              }))}
              title="Productos Relacionados"
              subtitle=""
              viewAll={false}
              url=""
            />
          </div>
        )}

        {showBackToTop && (
          <button
            className={styles.backToTop}
            onClick={scrollToTop}
            aria-label="Volver arriba"
          >
            <ArrowUpward />
          </button>
        )}
      </article>
    </>
  );
};

export default BlogArticle;
